import React, { useState, useEffect, useRef } from "react";
import socket from "../connection"; // Import the central socket instance
import { useParams, useNavigate } from "react-router-dom";
import { useAuthcontext } from "../Contexts/AuthContext";
import Chat from "./Chat";
import YouTube from "react-youtube";
import { useAppContext } from "../Contexts/AppContext";

function Ytplayer() {
  const { id, room } = useParams();

  const [sessionId, setSessionId] = useState(room);
  const [player, setPlayer] = useState(null);
  const [host, setHost] = useState(null); // State to maintain the host
  const playerRef = useRef(null);
  const { TheatorUser } = useAuthcontext();
  const navigate = useNavigate();

  const { videoId, setVideoId } = useAppContext();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("TheatorUser"));
    if (!user) {
      navigate("/login");
    }
  }, [TheatorUser, navigate]);
  useEffect(() => {
    if (id) {
      setVideoId(id);
    }
  }, [id, setVideoId]);

  useEffect(() => {
    const handleStateChange = (event) => {
      const playerState = event.data;
      switch (playerState) {
        case window.YT.PlayerState.PLAYING:
          playVideo();
          break;
        case window.YT.PlayerState.PAUSED:
          pauseVideo();
          break;
        default:
          break;
      }
    };

    if (player) {
      player.addEventListener("onStateChange", handleStateChange);
    }

    return () => {
      if (player) {
        document.removeEventListener("onStateChange", handleStateChange);
        window.removeEventListener("onStateChange", handleStateChange);
      }
    };
  }, [player, sessionId]);

  useEffect(() => {
    socket.emit("newuser", { sessionId, videoId });

    socket.on("control", (data) => {
      if (player) {
        switch (data.action) {
          case "play":
            player.playVideo();
            break;
          case "pause":
            player.pauseVideo();
            break;
          case "seek":
            if (player.getPlayerState() !== -1) {
              player.seekTo(data.time, true);
            } else {
              const checkReady = setInterval(() => {
                if (player.getPlayerState() !== -1) {
                  player.seekTo(data.time, true);
                  clearInterval(checkReady);
                }
              }, 100);
            }
            break;
          default:
            console.log("Unknown action");
        }
      }

      if (data.action === "play" || data.action === "seek") {
        if (player) {
          const currentTime = player.getCurrentTime();
          const timeDifference = Math.abs(data.time - currentTime);
          if (timeDifference > 1) {
            player.seekTo(data.time, true);
          }
        }
      }
    });

    socket.on("newUserJoined", () => {
      if (player) {
        const currentTime = player.getCurrentTime();
        const state = player.getPlayerState();
        const action = state === 1 ? "play" : "pause";
        socket.emit("currentState", {
          videoId,
          sessionId,
          action,
          time: currentTime,
        });
      }
    });

    socket.on("newHost", ({ newHost }) => {
      setHost(newHost);
      socket.emit("requestInitialPlaybackTime", { sessionId });
    });

    socket.on("currentState", ({ action, time }) => {
      if (player) {
        switch (action) {
          case "play":
            player.playVideo();
            break;
          case "pause":
            player.pauseVideo();
            break;
          case "seek":
            player.seekTo(time, true);
            break;
          default:
            console.log("Unknown action");
        }
      }
    });

    socket.on("videoChange", ({ vId, action, time }) => {
      setVideoId(vId);
      navigate(`/video/${vId}/${room}`);
      if (player && action === "play") {
        player.seekTo(time, true);
        player.playVideo();
      } else if (player && action === "pause") {
        player.seekTo(time, true);
        player.pauseVideo();
      }
    });

    socket.on("changePlaybackSpeed-ans", (rate) => {
      player?.setPlaybackRate(rate);
    });

    return () => {
      socket.off("control");
      socket.off("newUserJoined");
      socket.off("newHost");
      socket.off("currentState");
      socket.off("requestInitialPlaybackTime");
      socket.off("videoChange");
      socket.off("changePlaybackSpeed-ans");
    };
  }, [sessionId, player, videoId, setVideoId]);

  const onPlayerReady = (event) => {
    setPlayer(event.target);
    playerRef.current = event.target;
  };

  const playVideo = () => {
    const currentTime = player?.getCurrentTime();
    socket.emit("play", { sessionId, time: currentTime, userName: TheatorUser?.name });
  };

  const pauseVideo = () => {
    const currentTime = player?.getCurrentTime();
    socket.emit("pause", { sessionId, time: currentTime, userName: TheatorUser?.name });
  };

  const seekForward = () => {
    if (!player) return;
    const currentTime = player?.getCurrentTime();
    const newTime = currentTime + 30;
    socket.emit("seek", { sessionId, time: newTime, userName: TheatorUser?.name });
  };

  const seekBackward = () => {
    if (!player) return;
    const currentTime = player?.getCurrentTime();
    const newTime = currentTime - 30;
    socket.emit("seek", { sessionId, time: newTime, userName: TheatorUser?.name });
  };

  const onPlayerMuteChange = (event) => {
    const isMuted = event.target.isMuted();
    socket.emit("playerMuteChange", { sessionId, isMuted });
  };

  const opts = {
    height: "100%",
    width: "100%",
    playerVars: {
      autoplay: 1,
      mute: 0,
      rel: 0,
    },
  };

  const changePlaybackSpeed = (rate) => {
    if (player) {
      player.setPlaybackRate(rate.data);
      socket.emit("changePlaybackSpeed", { sessionId, rate: rate.data });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col gap-8 lg:flex-row h-fit">
        <div className="w-full lg:w-2/3">
          <div className="bg-black rounded-2xl overflow-hidden shadow-2xl aspect-video border-4 border-white">
            <YouTube
              key={videoId}
              videoId={videoId}
              opts={opts}
              onReady={onPlayerReady}
              onMute={onPlayerMuteChange}
              onUnmute={onPlayerMuteChange}
              onPlaybackRateChange={changePlaybackSpeed}
              className="w-full h-full"
            />
          </div>
          <div className="mt-6 p-4 bg-gray-50 rounded-2xl shadow-inner border border-gray-100 flex justify-center flex-wrap gap-3">
            <button
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-md active:scale-95"
              onClick={playVideo}
            >
              Play
            </button>
            <button
              className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-full transition-all shadow-md active:scale-95"
              onClick={pauseVideo}
            >
              Pause
            </button>
            <button
              className="px-8 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-bold rounded-full transition-all shadow-md active:scale-95"
              onClick={() => seekBackward()}
            >
              -30s
            </button>
            <button
              className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-full transition-all shadow-lg active:scale-95"
              onClick={() => seekForward()}
            >
              +30s
            </button>
          </div>
        </div>
        <div className="w-full lg:w-1/3 flex flex-col h-[500px] lg:h-[600px] max-h-[85vh]">
          <Chat />
        </div>
      </div>
    </div>
  );
}

export default Ytplayer;
